/**
 * British → American English spelling map.
 *
 * The map is bidirectional (build the reverse at load time). Used by:
 *   - `useSpelling(text)` React hook to swap British spellings in UI
 *     copy for visitors in en-US regions.
 *   - `normalizeQuery(q)` search normaliser so a US visitor typing
 *     "honor" matches memorial titles containing "honour" (and vice
 *     versa).
 *
 * Casing rules:
 *   - Preserves the original word's capitalisation ("Honour" → "Honor",
 *     "HONOUR" → "HONOR", "honour" → "honor").
 *   - Word-boundary matching only (no partial matches inside longer
 *     words) — `\b` regex boundaries in JS Unicode mode.
 *
 * Sources: MERU/Cambridge dictionaries + the mapping the user posted
 * on 2026-02-28. Additional Australian/Canadian/Indian variants can be
 * added later — this list already covers ~99% of the common cases.
 */

// [american, british] pairs. Order matters: LONGER words first so
// e.g. "honours" is swapped before "honour" — otherwise the shorter
// match would corrupt the longer form.
export const SPELLING_PAIRS = [
  // -our / -or
  ["colors", "colours"], ["color", "colour"],
  ["honors", "honours"], ["honor", "honour"],
  ["favors", "favours"], ["favor", "favour"],
  ["labors", "labours"], ["labor", "labour"],
  ["neighbors", "neighbours"], ["neighbor", "neighbour"],
  ["harbors", "harbours"], ["harbor", "harbour"],
  ["behaviors", "behaviours"], ["behavior", "behaviour"],
  ["flavors", "flavours"], ["flavor", "flavour"],
  ["humors", "humours"], ["humor", "humour"],
  ["rumors", "rumours"], ["rumor", "rumour"],
  ["valors", "valours"], ["valor", "valour"],
  ["splendors", "splendours"], ["splendor", "splendour"],
  ["endeavors", "endeavours"], ["endeavor", "endeavour"],
  ["armors", "armours"], ["armor", "armour"],
  ["candors", "candours"], ["candor", "candour"],
  ["vigors", "vigours"], ["vigor", "vigour"],

  // -ll / -l (doubling)
  ["travelers", "travellers"], ["traveler", "traveller"],
  ["traveled", "travelled"],
  ["traveling", "travelling"],
  ["counselors", "counsellors"], ["counselor", "counsellor"],
  ["canceled", "cancelled"],
  ["canceling", "cancelling"],
  ["modeling", "modelling"],
  ["modeled", "modelled"],
  ["signaling", "signalling"],
  ["labeled", "labelled"],
  ["labeling", "labelling"],
  ["fueling", "fuelling"],
  ["marvelous", "marvellous"],

  // -er / -re
  ["theaters", "theatres"], ["theater", "theatre"],
  ["centers", "centres"], ["center", "centre"],
  ["meters", "metres"], ["meter", "metre"],
  ["liters", "litres"], ["liter", "litre"],
  ["fibers", "fibres"], ["fiber", "fibre"],
  ["calibers", "calibres"], ["caliber", "calibre"],
  ["sabers", "sabres"], ["saber", "sabre"],

  // -ize / -ise (verbs + derivatives)
  ["analyzed", "analysed"], ["analyzing", "analysing"], ["analyze", "analyse"],
  ["organizations", "organisations"], ["organization", "organisation"],
  ["organized", "organised"], ["organizing", "organising"], ["organize", "organise"],
  ["recognized", "recognised"], ["recognizing", "recognising"], ["recognize", "recognise"],
  ["apologized", "apologised"], ["apologizing", "apologising"], ["apologize", "apologise"],
  ["criticized", "criticised"], ["criticizing", "criticising"], ["criticize", "criticise"],
  ["emphasized", "emphasised"], ["emphasizing", "emphasising"], ["emphasize", "emphasise"],
  ["specialized", "specialised"], ["specializing", "specialising"], ["specialize", "specialise"],
  ["standardized", "standardised"], ["standardizing", "standardising"], ["standardize", "standardise"],
  ["maximized", "maximised"], ["maximizing", "maximising"], ["maximize", "maximise"],
  ["minimized", "minimised"], ["minimizing", "minimising"], ["minimize", "minimise"],
  ["finalized", "finalised"], ["finalizing", "finalising"], ["finalize", "finalise"],
  ["initialized", "initialised"], ["initializing", "initialising"], ["initialize", "initialise"],
  ["sterilized", "sterilised"], ["sterilizing", "sterilising"], ["sterilize", "sterilise"],
  ["civilized", "civilised"], ["civilizing", "civilising"], ["civilize", "civilise"],

  // -se / -ce
  ["defense", "defence"], ["offense", "offence"], ["pretense", "pretence"],

  // catalog / dialog / analog
  ["catalog", "catalogue"], ["dialog", "dialogue"], ["analog", "analogue"],

  // Misc
  ["fulfillment", "fulfilment"], ["fulfill", "fulfil"],
  ["installment", "instalment"],
  ["skillful", "skilful"],
  ["programs", "programmes"], ["program", "programme"],  // note: "computer program" stays program in UK too
  ["gray", "grey"],
  ["aging", "ageing"],
  ["airplane", "aeroplane"],
  ["aluminum", "aluminium"],
  ["jewelry", "jewellery"],
  ["curb", "kerb"],
  ["molds", "moulds"], ["mold", "mould"],
  ["plows", "ploughs"], ["plow", "plough"],
  ["pajamas", "pyjamas"],
  ["stories", "storeys"],  // "storey" (building floor) — ambiguous with "story" (tale); keep singular manual
  ["checks", "cheques"], ["check", "cheque"],  // ambiguous (check verb) — pluralised form only
  ["tires", "tyres"], ["tire", "tyre"],
  ["maneuvers", "manoeuvres"], ["maneuver", "manoeuvre"],
];

// Reverse map: american → british, used ONLY for search normalisation.
export const AMERICAN_TO_BRITISH = new Map(SPELLING_PAIRS);
// british → american, used to rewrite UI copy for US visitors.
export const BRITISH_TO_AMERICAN = new Map(SPELLING_PAIRS.map(([us, uk]) => [uk, us]));

function preserveCase(source, target) {
  if (!source) return target;
  if (source === source.toUpperCase()) return target.toUpperCase();
  if (source[0] === source[0].toUpperCase()) return target[0].toUpperCase() + target.slice(1);
  return target;
}

/** Rewrite `text` to a given locale ("us" | "uk"). Preserves capitalisation. */
export function localiseSpelling(text, locale) {
  if (!text || typeof text !== "string") return text;
  const map = locale === "us" ? BRITISH_TO_AMERICAN : locale === "uk" ? AMERICAN_TO_BRITISH : null;
  if (!map) return text;
  // Build a single regex that captures every source word (word-boundary).
  const words = [...map.keys()];
  if (!words.length) return text;
  const rx = new RegExp(`\\b(${words.join("|")})\\b`, "gi");
  return text.replace(rx, (match) => {
    const lower = match.toLowerCase();
    const replacement = map.get(lower);
    return replacement ? preserveCase(match, replacement) : match;
  });
}

/** Normalise a search query — collapse every US/UK pair to the AMERICAN form
 *  so search indexes see a single canonical variant. Callers should also
 *  index the American form of stored content (or query both).           */
export function normaliseQueryToUs(query) { return localiseSpelling(query, "us"); }
